import { Chain, Environment } from '@shared/data-access/interfaces';
import { CustomHttpHeaders, MediaType } from '@shared/data-access/enums';
import {
  GetRecommendationsDaoOptions,
  IRecommendationsResponse,
} from './../interfaces/recommendations.interface';
/** @format */
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Observable, shareReplay } from 'rxjs';
import { isPlatformServer } from '@angular/common';

import { ContainerId } from './../constants/container-id.constants';
import { DCSG_CONSTANTS } from '@shared/data-access/constants';
import { ProductRecommendationsPageType } from './../constants/product-recommendations-page-type.constants';
import { WINDOW } from '@shared/data-access/services';

@Injectable({
  providedIn: 'root',
})
export class ProductRecommendationsDao {
  private readonly cacheMap = new Map<string, Observable<IRecommendationsResponse>>();
  private readonly chain = inject(Chain);
  private readonly environment = inject(Environment);
  private readonly http = inject(HttpClient);
  protected readonly platformId = inject(PLATFORM_ID);
  protected readonly window = inject<Window>(WINDOW);

  /**
   * @description
   * Calls the ds-recs-details application with a specifically formatted request body and returns a list of recommended products
   * with their associated details already mapped.
   *
   * @param requestBody -- The request body to send to the ds-recs-details microservice
   * @param placementId -- The placement ID string of the type of recommendations to get back (e.g. others-you-may-like)
   * @param maxNumberOfProductsToReturn -- The maximum number of recommended products to return
   * @param project
   * @param returnAllProductDetails -- Flag to determine whether to return all properties from Elastic
   * @param returnOutOfStockProducts -- Flag to determine whether to return out-of-stock products in the recommendations list
   * @param pageType -- The name of the page where recommendations are displayed
   * @param containerID -- String identifier for the type of recs - (e.g. YMAL, PLA)
   */
  getRecommendationsWithProductDetails$({
    requestBody,
    placementId,
    maxNumberOfProductsToReturn,
    project,
    returnAllProductDetails,
    returnOutOfStockProducts,
    pageType,
    containerID,
    dsgRecs,
    timeoutMs,
  }: GetRecommendationsDaoOptions): Observable<IRecommendationsResponse> {
    const cacheKey = JSON.stringify({
      requestBody,
      placementId,
      maxNumberOfProductsToReturn,
      project,
      returnAllProductDetails,
      returnOutOfStockProducts,
      pageType,
      containerID,
      dsgRecs,
      timeoutMs,
    });
    // Standard HTTP Headers
    let httpHeaders = new HttpHeaders({
      Accept: MediaType.APPLICATION_JSON,
    });

    // Override default Timeout specified in HttpInterceptor
    httpHeaders = httpHeaders.set(
      CustomHttpHeaders.X_TIMEOUT,
      `${timeoutMs ?? DCSG_CONSTANTS.timeouts.medium}`,
    );

    // For relevance team
    if (
      pageType === ProductRecommendationsPageType.CART ||
      pageType === ProductRecommendationsPageType.ORDER_CONFIRMATION ||
      containerID === ContainerId.YMAL ||
      containerID === ContainerId.PLA ||
      pageType === ProductRecommendationsPageType.ORDER_DETAILS ||
      containerID === ContainerId.FAVORITES_FBT
    ) {
      const poolCSwimlane: string | null = this.getPoolCSwimlane();
      if (poolCSwimlane) {
        httpHeaders = httpHeaders.set(CustomHttpHeaders.POOL_C_SWIMLANE, poolCSwimlane);
      }
    }

    // Query String Parameters
    let httpParams = new HttpParams()
      .set('placementId', placementId)
      .set('storeId', this.chain.storeId)
      .set('maxNumberOfProductsToReturn', maxNumberOfProductsToReturn)
      .set('returnAllProductDetails', returnAllProductDetails)
      .set('returnOutOfStockProducts', returnOutOfStockProducts)
      .set('project', project);
    if (typeof dsgRecs === 'string' && dsgRecs.trim() !== '') {
      httpParams = httpParams.set('flag', dsgRecs);
    }

    let cartContainerId = ContainerId.CART_FBT;
    let orderConfirmationContainerId = ContainerId.ORDERCONFIRM_FBT;
    switch (this.chain.chainIdentifierAbbr) {
      case 'pl':
        cartContainerId = ContainerId.PL_CART_FBT;
        orderConfirmationContainerId = ContainerId.PL_ORDERCONFIRM_FBT;
        break;
      case 'gg':
        cartContainerId = ContainerId.GG_CART_FBT;
        orderConfirmationContainerId = ContainerId.GG_ORDERCONFIRM_FBT;
        break;
    }

    // For relevance team
    switch (pageType) {
      case ProductRecommendationsPageType.CART:
        httpParams = httpParams.set(ContainerId.ID, cartContainerId);
        break;
      case ProductRecommendationsPageType.ORDER_CONFIRMATION:
        httpParams = httpParams.set(ContainerId.ID, orderConfirmationContainerId);
        break;
      case ProductRecommendationsPageType.ORDER_DETAILS:
        httpParams = httpParams.set(ContainerId.ID, ContainerId.ORDERHISTORY_FBT);
        break;
      case ProductRecommendationsPageType.PDP:
        if (containerID === ContainerId.YMAL || containerID === ContainerId.PDP_YMAL) {
          httpParams = httpParams.set(ContainerId.ID, ContainerId.PDP_YMAL);
        } else if (containerID === ContainerId.PDP_YMAL_DSG) {
          httpParams = httpParams.set(ContainerId.ID, ContainerId.PDP_YMAL_DSG);
        } else if (containerID === ContainerId.PDP_YMAL_DSG_EXTERNAL) {
          httpParams = httpParams.set(ContainerId.ID, ContainerId.PDP_YMAL_DSG_EXTERNAL);
        } else if (containerID === ContainerId.PLA || containerID === ContainerId.PDP_PLA) {
          httpParams = httpParams.set(ContainerId.ID, ContainerId.PDP_PLA);
        } else if (containerID === ContainerId.PDP_ATC || containerID === ContainerId.PDP_ATC_DSG) {
          httpParams = httpParams.set(ContainerId.ID, containerID);
        }
        break;
      case ProductRecommendationsPageType.FAVORITES:
        httpParams = httpParams.set(ContainerId.ID, ContainerId.FAVORITES_FBT);
        break;
    }

    // HTTP Options
    const httpOptions = {
      headers: httpHeaders,
      params: httpParams,
    };

    const endpoint = this.environment.api.spring.microServiceProxy.recommendations?.path;
    const baseDomain = isPlatformServer(this.platformId)
      ? (this.environment.api.dataScience?.baseDomain?.[this.chain.chainIdentifierAbbr] ?? '')
      : '';

    const cached$ = this.cacheMap.get(cacheKey);

    if (cached$) {
      return cached$;
    }

    // Make the HTTP Request
    const response = this.http
      .post<IRecommendationsResponse>(
        `${baseDomain}${endpoint}recommendations/details`,
        requestBody,
        httpOptions,
      )
      .pipe(shareReplay(1));

    this.cacheMap.set(cacheKey, response);

    return response;
  }

  public getPoolCSwimlane(): string | null {
    const swimLanes = this.window?._dsgTag?.customerLanes;
    if (swimLanes && swimLanes.length >= 3) {
      return String(swimLanes[2]);
    }
    return '';
  }
}
